import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React from 'react';

interface SpliceNavigationProps {
  selectedItem?: {
    type: string;
    name: string;
    status: string;
    nextSplice: string;
  };
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'critical':
      return 'bg-red-600';
    case 'alarm':
      return 'bg-orange-500';
    case 'monitor':
      return 'bg-yellow-500';
    case 'healthy':
      return 'bg-green-600';
    default:
      return 'bg-gray-500';
  }
};

const getStatusTextColor = (status: string) => {
  switch (status) {
    case 'critical':
      return 'text-red-600';
    case 'alarm':
      return 'text-orange-500';
    case 'monitor':
      return 'text-yellow-500';
    case 'healthy':
      return 'text-green-600';
    default:
      return 'text-gray-500';
  }
};

export function SpliceNavigation({ selectedItem, onPrevious, onNext, hasPrevious, hasNext }: SpliceNavigationProps) {
  if (!selectedItem) {
    return (
      <div className="flex h-24 w-full items-center justify-center text-gray-500">
        Select an item on the belt to view details
      </div>
    );
  }

  const isCircle = selectedItem.type === 'S';

  return (
    <div className="flex h-24 w-full items-center justify-between px-4 min-[1900px]:justify-center flex-shrink-0">
      {/* On screens > 1900px, constrain max width */}
      <div className="flex items-center justify-between gap-8 w-full min-[1900px]:max-w-2xl">
        {/* Left Arrow - Fixed position */}
        <Button
          className="w-12 h-12 text-white hover:text-gray-300 border-2 border-gray-400 p-0 bg-transparent hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          onClick={onPrevious}
          disabled={!hasPrevious}
        >
          <ChevronLeft size={48} />
        </Button>

        {/* Center Content - Flexible width with min/max constraints */}
        <div className="flex flex-col gap-1 items-center flex-1 min-w-0">
          <div className="text-sm flex items-center gap-2 justify-center">
            {/* INDICATOR */}
            <div
              className={`${isCircle ? 'rounded-full' : 'rounded'} ${getStatusColor(
                selectedItem.status
              )} h-8 w-8 items-center justify-center flex font-semibold text-white flex-shrink-0`}
            >
              {selectedItem.type}
            </div>
            <div className="text-secondary font-semibold whitespace-nowrap">{selectedItem.name}</div>
            {/* HEALTH STATUS */}
            <div className={`${getStatusTextColor(selectedItem.status)} font-semibold text-xs flex capitalize`}>
              {selectedItem.status}
            </div>
          </div>
          <div className="text-gray-300 text-sm text-center">
            Distance to next defect: <span className="text-white">{selectedItem.nextSplice || 'N/A'}</span>
          </div>
        </div>

        {/* Right Arrow - Fixed position */}
        <Button
          className="w-12 h-12 text-white hover:text-gray-300 border-2 border-gray-400 p-0 bg-transparent hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          onClick={onNext}
          disabled={!hasNext}
        >
          <ChevronRight size={48} />
        </Button>
      </div>
    </div>
  );
}
